/*
 * The setup sheet.
 *
 * Rendered entirely from /api/registry, so adding a field is one line of Python
 * and nothing here changes.
 *
 * Three things this screen has to get right:
 *
 * 1. Prefill. It arrives carrying the previous session's setup, because a
 *    normal session is "change the one thing you changed, save". Losing that
 *    would mean re-entering fifty-nine fields at a race meeting.
 * 2. Changed fields are visually distinct, with the previous value shown
 *    underneath. Setup comparison is the whole point of the app, so a change
 *    has to be obvious before you commit it, not after.
 * 3. Save is always reachable - sticky at the bottom - and the form keeps its
 *    contents if the save fails. Nothing typed at the track is ever thrown away
 *    by a dropped connection.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { ChevronDown, ChevronRight, Save } from 'lucide-react'
import { ApiError, api } from '../lib/api'
import type { ChangeAdvice, FieldDef, GaragePart, Registry } from '../lib/api'
import { Spinner } from '../components/bits'
import { lapTime, layoutName, shortDate } from '../lib/format'

const DRAFT_PREFIX = 'gk-setup-draft-'

/**
 * What was run at this same layout last time.
 *
 * The fields shown are the ones that do NOT carry over from another circuit -
 * gearing above all. Everything else the kart is already wearing, so repeating
 * it here would be noise.
 */
const TRAVELS_BADLY = [
  'sprocket_rear', 'sprocket_front', 'jet_main', 'carb_idle_jet',
  'carb_needle_clip', 'spark_plug', 'rear_track', 'front_track',
]

function LastTimeHere({ here, fields, onApply }: {
  here: {
    run_id: number; started_at: string | null; timezone: string
    best_lap_s: number | null; race_class: string | null
    layout: string | null; values: Record<string, string>
  }
  fields: FieldDef[]
  onApply: (values: Record<string, string>) => void
}) {
  const shown = TRAVELS_BADLY
    .map((key) => [key, here.values[key]] as const)
    .filter(([, v]) => String(v ?? '').trim())
  if (shown.length === 0) return null

  const label = (key: string) =>
    fields.find((f) => f.key === key)?.label ?? key
  const unit = (key: string) => fields.find((f) => f.key === key)?.unit ?? ''

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-head">
        <h2>Last time here</h2>
        <span className="small dim">
          {here.layout} · run {here.run_id} · {shortDate(here.started_at, here.timezone)}
          {here.best_lap_s && ' · '}
          {here.best_lap_s && <b className="lap best">{lapTime(here.best_lap_s)}</b>}
          {here.race_class && ` · ${here.race_class}`}
        </span>
      </div>
      <div className="row wrap" style={{ gap: 18 }}>
        {shown.map(([key, value]) => (
          <div key={key}>
            <div className="tiny dim">{label(key)}</div>
            <div style={{ fontWeight: 650 }}>
              {value}{unit(key) ? ` ${unit(key)}` : ''}
            </div>
          </div>
        ))}
      </div>
      <div className="between wrap" style={{ marginTop: 12 }}>
        <span className="small dim">
          These are the settings that do not travel between circuits. The rest
          is already carried forward from your last session.
        </span>
        <button className="btn ghost small"
          onClick={() => onApply(Object.fromEntries(shown))}>
          Use these
        </button>
      </div>
    </div>
  )
}

export function SetupForm() {
  const { id } = useParams()
  const navigate = useNavigate()

  const registry = useQuery({
    queryKey: ['registry'],
    queryFn: () => api.get<Registry>('/api/registry'),
  })
  const current = useQuery({
    queryKey: ['setup', id],
    queryFn: () => api.get<{
      values: Record<string, string>
      prefill: Record<string, string>
      prefilled_from: number | null
      has_setup: boolean
      last_time_here: {
        session_id: number; run_id: number; started_at: string | null
        timezone: string; best_lap_s: number | null; race_class: string | null
        layout: string | null; values: Record<string, string>
      } | null
    }>(`/api/sessions/${id}/setup`),
  })
  // Headed by the Race Studio run number, not the internal row id - "run 707"
  // is what is written on the timing sheet and what the mechanic recognises.
  const session = useQuery({
    queryKey: ['session', id],
    queryFn: () => api.get<{ run_id: number; track: { name: string } | null
      layout: { name: string; direction: string } | null }>(`/api/sessions/${id}`),
  })
  const parts = useQuery({
    queryKey: ['garage', 'all'],
    queryFn: async () => {
      const cats = await api.get<{ categories: { key: string }[] }>('/api/garage')
      const lists = await Promise.all(cats.categories.map((c) =>
        api.get<{ category: string; parts: GaragePart[] }>(`/api/garage/${c.key}`)))
      return lists
    },
  })

  const [values, setValues] = useState<Record<string, string>>({})
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [advice, setAdvice] = useState<ChangeAdvice | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Baseline for "what changed": the previous session when this is a new
  // setup, or what is already saved when editing one.
  const baseline = useMemo(() => {
    if (!current.data) return {}
    return current.data.has_setup ? current.data.values : current.data.prefill
  }, [current.data])

  useEffect(() => {
    if (!current.data) return
    // A draft survives the tab reloading or the phone dying mid-entry, which
    // matters with a fifty-nine field form open in a paddock.
    const draft = localStorage.getItem(DRAFT_PREFIX + id)
    if (draft) {
      try { setValues(JSON.parse(draft)); return } catch { /* fall through */ }
    }
    setValues({ ...baseline, ...current.data.values })
  }, [current.data, baseline, id])

  useEffect(() => {
    if (Object.keys(values).length) {
      localStorage.setItem(DRAFT_PREFIX + id, JSON.stringify(values))
    }
  }, [values, id])

  const save = useMutation({
    mutationFn: () => api.put<{ advice: ChangeAdvice }>(
      `/api/sessions/${id}/setup`, { values }),
    onSuccess: (data) => {
      setAdvice(data.advice)
      setError(null)
      localStorage.removeItem(DRAFT_PREFIX + id)
    },
    onError: (e: unknown) => {
      // Keep everything typed. A failed save must never cost the mechanic
      // their entry - they can hit save again once signal returns.
      setError(e instanceof ApiError ? e.message : 'Could not save. Try again.')
    },
  })

  if (registry.isLoading || current.isLoading) {
    return <div className="page"><Spinner /></div>
  }

  const reg = registry.data!
  const partsByCategory = new Map(
    (parts.data ?? []).map((l) => [l.category, l.parts]))

  const optionsFor = (f: FieldDef): string[] => {
    if (!f.source) return f.options
    const [cat, position] = f.source.split(':')
    const list = partsByCategory.get(cat!) ?? []
    return list
      .filter((p) => !position || p.position === position)
      .map((p) => p.name)
  }

  const changedKeys = Object.keys(values).filter((k) => {
    const before = String(baseline[k] ?? '').trim()
    const now = String(values[k] ?? '').trim()
    return before !== now && (before || now)
  })

  return (
    <div className="page" style={{ paddingBottom: 120 }}>
      <div className="page-head">
        <div>
          <h1>
            {session.data ? layoutName(session.data.track, session.data.layout) : 'Setup'}
            {session.data && ` — run ${session.data.run_id}`}
          </h1>
          <div className="sub">
            {current.data?.prefilled_from && !current.data.has_setup
              ? 'Prefilled from your previous setup. Only change what changed for this session.'
              : current.data?.has_setup
                ? 'Editing the setup already saved for this session.'
                : 'No previous setup to prefill from.'}
          </div>
        </div>
        {changedKeys.length > 0 && (
          <span className="badge partial">
            {changedKeys.length} change{changedKeys.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* What worked HERE last time - a different question from prefill,
          which is what happens to be bolted to the kart right now. Gearing
          especially does not travel between circuits. */}
      {current.data?.last_time_here && (
        <LastTimeHere here={current.data.last_time_here} fields={reg.fields}
          onApply={(vals) => setValues((v) => ({ ...v, ...vals }))} />
      )}

      {reg.groups.map((group) => {
        const fields = reg.fields.filter((f) => f.group === group)
        const changedHere = fields.filter((f) => changedKeys.includes(f.key)).length
        const isOpen = open[group] ?? true
        return (
          <div className="card" key={group} style={{ marginBottom: 12 }}>
            <button className="between"
              style={{ background: 'none', border: 0, color: 'inherit',
                       width: '100%', cursor: 'pointer', padding: 0 }}
              onClick={() => setOpen((o) => ({ ...o, [group]: !isOpen }))}>
              <h2 className="row" style={{ gap: 8 }}>
                {isOpen ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                {group}
              </h2>
              {changedHere > 0 && (
                <span className="badge partial">
                  {changedHere} change{changedHere === 1 ? '' : 's'}
                </span>
              )}
            </button>

            {isOpen && (
              <div style={{ marginTop: 14 }}>
                {fields.map((f) => {
                  const value = values[f.key] ?? ''
                  const was = String(baseline[f.key] ?? '').trim()
                  const isChanged = changedKeys.includes(f.key)
                  const opts = optionsFor(f)
                  return (
                    <label className={clsx('field', isChanged && 'changed')} key={f.key}>
                      <span className="name">
                        {f.label}{f.unit ? ` (${f.unit})` : ''}
                      </span>

                      {f.kind === 'choice' ? (
                        <select value={value}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}>
                          <option value=""></option>
                          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                          {value && !opts.includes(value) && (
                            <option value={value}>{value}</option>
                          )}
                        </select>
                      ) : f.kind === 'textarea' ? (
                        <textarea value={value}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
                      ) : (
                        <input
                          type={f.kind === 'num' ? 'number' : 'text'}
                          inputMode={f.kind === 'num' ? 'decimal' : undefined}
                          step="any" value={value}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
                      )}

                      {f.source && opts.length === 0 && (
                        <span className="hint">
                          Nothing in the garage yet — add one under Garage.
                        </span>
                      )}
                      {f.hint && <span className="hint">{f.hint}</span>}
                      {isChanged && was && <span className="was">Previous: {was}</span>}
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {advice && (
        <div className={clsx('advice', advice.level)}>{advice.message}</div>
      )}
      {error && <div className="advice warn">{error}</div>}

      <div style={{
        position: 'sticky', bottom: 0, padding: '14px 0',
        background: 'linear-gradient(transparent, var(--bg) 30%)',
      }}>
        <div className="row" style={{ gap: 10 }}>
          <button className="btn" disabled={save.isPending}
            onClick={() => save.mutate()}>
            <Save size={16} /> {save.isPending ? 'Saving…' : 'Save setup'}
          </button>
          <button className="btn ghost" onClick={() => navigate(`/sessions/${id}`)}>
            Back to session
          </button>
        </div>
      </div>
    </div>
  )
}
